import { Spell, SpellCategory } from '../types';
import spellsData from '../data/spells.json';
import spellCategoriesData from '../data/spellCategories.json';
import schoolsData from '../data/schools.json';
import { applySchoolAndLevelUps, applyBaseResourceCastingBonuses, categoriseSpellsBySchool } from '../utils/spellCastingNumbers';
import { loadWarbandData } from './localStorageService';

export interface SpellSchool {
  id: string;
  school: string;
  description: string;
}

type RawSpell = Omit<Spell, 'categories'> & { categoryIds: string[] };

const spellCategoriesMap = new Map<string, SpellCategory>(
  (spellCategoriesData as Array<{ id: string; description: string }>).map(c => [
    c.id,
    { id: c.id, description: c.description },
  ])
);

export const allSpells: Spell[] = (spellsData as unknown as RawSpell[]).map(s => {
  const { categoryIds, ...rest } = s;
  return { ...rest, categories: categoryIds.map(id => spellCategoriesMap.get(id)!).filter(Boolean) };
});

const allSchools = (schoolsData as Array<{ id: string; school: string; description: string }>).map(
  (s): SpellSchool => ({ id: s.id, school: s.school, description: s.description })
);

export const spellService = {
  clearCache: () => {},
  clearWarbandCache: (_warbandId: string) => {},

  getAllAvailableForWarband: (warbandId: string) => {
    const data = loadWarbandData(warbandId);
    const schoolId = data?.warband.schoolId ?? '';
    const baseResourceIds = data?.selectedBaseResourceIds ?? [];
    const adjusted = applyBaseResourceCastingBonuses(
      applySchoolAndLevelUps(allSpells, schoolId),
      baseResourceIds
    );
    return Promise.resolve({ data: adjusted.sort((a, b) => a.name.localeCompare(b.name)) });
  },

  getAllForWarband: (warbandId: string) => {
    const data = loadWarbandData(warbandId);
    const selectedIds = new Set(data?.selectedSpellIds ?? []);
    const selected = allSpells.filter((s) => selectedIds.has(s.id));
    const schoolId = data?.warband.schoolId ?? '';
    const baseResourceIds = data?.selectedBaseResourceIds ?? [];
    const improvements: Record<string, number> = {};
    for (const lu of data?.levelUps ?? []) {
      if (lu.spellImprovement) {
        improvements[lu.spellImprovement] = (improvements[lu.spellImprovement] ?? 0) + 1;
      }
    }
    const adjusted = applyBaseResourceCastingBonuses(
      applySchoolAndLevelUps(selected, schoolId, improvements),
      baseResourceIds
    );
    return Promise.resolve({ data: adjusted });
  },

  getByIdForWarband: (warbandId: string, id: string) => {
    const data = loadWarbandData(warbandId);
    const schoolId = data?.warband.schoolId ?? '';
    const baseResourceIds = data?.selectedBaseResourceIds ?? [];
    const spell = allSpells.find((s) => s.id === id);
    if (!spell) return Promise.resolve({ data: null as Spell | null });
    const [adjusted] = applyBaseResourceCastingBonuses(
      applySchoolAndLevelUps([spell], schoolId),
      baseResourceIds
    );
    return Promise.resolve({ data: adjusted });
  },

  getPrimaryForWarband: (warbandId: string) => {
    const data = loadWarbandData(warbandId);
    const schoolId = data?.warband.schoolId ?? '';
    const baseResourceIds = data?.selectedBaseResourceIds ?? [];
    const adjusted = applyBaseResourceCastingBonuses(
      applySchoolAndLevelUps(allSpells, schoolId),
      baseResourceIds
    );
    const { primarySpells } = categoriseSpellsBySchool(adjusted, schoolId);
    return Promise.resolve({ data: primarySpells });
  },

  getAlignedForWarband: (warbandId: string) => {
    const data = loadWarbandData(warbandId);
    const schoolId = data?.warband.schoolId ?? '';
    const baseResourceIds = data?.selectedBaseResourceIds ?? [];
    const adjusted = applyBaseResourceCastingBonuses(
      applySchoolAndLevelUps(allSpells, schoolId),
      baseResourceIds
    );
    const { alignedSpells } = categoriseSpellsBySchool(adjusted, schoolId);
    return Promise.resolve({ data: alignedSpells });
  },

  getNeutralForWarband: (warbandId: string) => {
    const data = loadWarbandData(warbandId);
    const schoolId = data?.warband.schoolId ?? '';
    const baseResourceIds = data?.selectedBaseResourceIds ?? [];
    const adjusted = applyBaseResourceCastingBonuses(
      applySchoolAndLevelUps(allSpells, schoolId),
      baseResourceIds
    );
    const { neutralSpells } = categoriseSpellsBySchool(adjusted, schoolId);
    return Promise.resolve({ data: neutralSpells });
  },

  getOpposedForWarband: (_warbandId: string) =>
    Promise.resolve({ data: [] as Spell[] }),

  getSchools: () => Promise.resolve({ data: allSchools }),
};